import React, { useContext, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './TenantPermittedDomains.scss';
import { PermittedDomainsReadOnly } from './PermittedDomainsReadOnly';
import { PermittedDomainsEditor } from './PermittedDomainsEditor';
import { H4 } from 'components/typography';
import { UserDataContext } from 'lane-shared/contexts';
import useChannelAdminContext from 'hooks/useChannelAdminContext';
import { PERMISSION_KEYS } from 'constants-permissions';
import { Button } from 'design-system-web';
import { useTenantDomainsMutation } from '../../hooks/useTenantDomainsMutation';
import ErrorMessage from 'components/general/ErrorMessage';

interface TenantChannelDetails {
    id?: string;
    filters?: string[];
}

interface Tenant {
    channelDetails?: TenantChannelDetails;
}

interface TenantPermittedDomainsProps {
    tenant?: Tenant;
}

export const TenantPermittedDomains: React.FC<TenantPermittedDomainsProps> = ({
    tenant,
}) => {
    const { t } = useTranslation();
    const { hasAnyPermission } = useContext(UserDataContext);
    const { channel } = useChannelAdminContext();

    // Get domains from channel.filters (trimmed and filtered)
    // Normalizes domain strings by removing whitespace and empty entries
    const currentDomains = useMemo(() => {
        if(tenant){
            return (tenant?.channelDetails?.filters || [])
            .map((f: string) => f.trim())
            .filter((f: string) => f !== '');
        }

        return (channel?.filters || [])
            .map((f) => f.trim())
            .filter((f) => f !== '');
    }, [channel?.filters, tenant?.channelDetails?.filters]);

    // State for edit mode
    const [isEditMode, setIsEditMode] = useState(false);
    const [domains, setDomains] = useState<string[]>(currentDomains);
    const [originalDomains, setOriginalDomains] = useState<string[]>(currentDomains);
    const [saveError, setSaveError] = useState<Error | null>(null);

    // Mutation hook for saving domains
    // Pass propertyId and tenantId for automatic refetch of tenant details
    const { saveDomains, loading: saveLoading, error: mutationError } = useTenantDomainsMutation(
        tenant?.channelDetails?.id ? tenant?.channelDetails?.id : channel?._id || '',
        channel?._id,
        tenant?.channelDetails?.id,
    );

    // Sync domains when channel.filters changes externally
    useEffect(() => {
        if (!isEditMode) {
            setDomains(currentDomains);
            setOriginalDomains(currentDomains);
        }
    }, [currentDomains, isEditMode]);

    const canEditDomains = channel 
        ? hasAnyPermission([PERMISSION_KEYS.PERMISSION_ADMIN, PERMISSION_KEYS.PERMISSION_PROPERTY_TENANT_DOMAIN_WHITELISTING_MANAGE], channel._id)
        : false;

    const handleEditClick = () => {
        setOriginalDomains(domains);
        setIsEditMode(true);
        setSaveError(null); // Reset error when entering edit mode
    };

    const handleSave = async (updatedDomains: string[]): Promise<{ success: boolean; error?: Error }> => {
        if (!channel?._id) {
            const error = new Error('Channel ID is required');

            setSaveError(error);

            return { success: false, error };
        }

        setSaveError(null);

        const result = await saveDomains(updatedDomains);

        if (result.success) {
            // Success flow: exit edit mode, toast shown by hook, refetch handled by mutation
            setIsEditMode(false);
            setDomains(updatedDomains);
            setOriginalDomains(updatedDomains);
            setSaveError(null); // Reset error on success
            
            // Show success toast
            if (window.Toast) {
                window.Toast.show(
                    t('web.admin.channel.profile.settings.permittedDomains.saveSuccess'),
                    3000
                );
            }
        } else {
            // Error flow: remain in edit mode, preserve changes
            const error = (result.error instanceof Error 
                ? result.error 
                : new Error(result.error ? String(result.error) : 'Failed to save domains')) as Error;
            
            // Show error toast
            if (window.Toast) {
                window.Toast.show(
                    t('web.admin.channel.profile.settings.permittedDomains.error'),
                    3000
                );
            }
            
            // Display inline error below Save button for validation/server errors
            setSaveError(error);
        }

        return { success: result.success, error: result.error as Error | undefined };
    };

    const handleCancel = () => {
        setDomains(originalDomains);
        setIsEditMode(false);
        setSaveError(null); // Reset error on cancel
    };

    return (
        <div data-testid="tenant-permitted-domains">
            <div className={styles.Header}>
                <div className={styles.SectionHeader}>
                    <H4 className={styles.Title}>{t('web.admin.channel.profile.settings.permittedDomains.title')}</H4>
                    {canEditDomains && !isEditMode && (
                        <Button
                            variant="secondary"
                            size="medium"
                            onClick={handleEditClick}
                            testId="tenantPermittedDomainsEditButton"
                        >
                            {t('web.admin.channel.profile.settings.permittedDomains.editButton')}
                        </Button>
                    )}
                </div>
                <div className={styles.Description}>
                    <p>{t('web.admin.channel.profile.settings.permittedDomains.tenantManagement.description.line1')}</p>
                    <p>{t('web.admin.channel.profile.settings.permittedDomains.description.line2')}</p>
                </div>
            </div>

            {isEditMode ? (
                <>
                    <PermittedDomainsEditor
                        domains={domains}
                        onSave={handleSave}
                        onCancel={handleCancel}
                        loading={saveLoading}
                        error={saveError || mutationError}
                    />
                    {(saveError || mutationError) && (
                        <div className={styles.ErrorContainer} data-testid="save-error">
                            <ErrorMessage error={saveError || mutationError} />
                        </div>
                    )}
                </>
            ) : (
                <PermittedDomainsReadOnly
                    domains={domains}
                    isEmpty={domains.length === 0}
                />
            )}
        </div>
    );
};

